import { createReducer } from "reduxsauce"
import actionCreator from "helpers/actionCreator"
import { takeEvery, takeLatest, take, call, select, put, all, race, delay } from "redux-saga/effects"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { Types as LoaderTypes, Creators as modelCreators } from "model/data"
import { Types as GQLLoaderTypes } from "model/graphql"
import { getPortfolioIdFromPathname } from "model/selected"
import { Types as HistoryTypes } from "../../../actions/app/history"
import { Types as DefaultMetricTypes } from "modules/default-metric-units/model"
import { omit, isEmpty, get, chain, pick, map, mapValues, includes, filter, set } from "lodash"
import { Types as PortfolioRootTypes, Creators as PortfolioRootCreators } from "modules/portfolio-root/model"
import { getSelectedModelId, getCurrency, getPortfolioId, getIsExpanded } from "modules/portfolio-root/selectors/portfolios"
import { getIsInvalidBetaInstrumentReason, getPortfolioBetaInstrument } from "modules/portfolio-root/selectors/beta-instrument"
import { getRebalanceDate, getActionsBase, getSimulateActions, getSimulatorSettings, getDecompositionType, getReset } from "./selectors"
import { getIsActiveAndNotIntraday, getIsIntraday, getIntradayBatchId, getIsIntradayRoute } from "./selectors/intraday"
import { getBasePositionSet } from "selectors/base"
import { getEndDate } from "selectors/dates"
import { getSelectedGroup, getGroupingOptions } from "./selectors/grouping"
import { sendQuery } from "api/graphql"
import { NORMALIZATION, computeFilteredSegment, removeUnsupportedFilters } from "helpers/segments"
import { getMissingDataFiles } from "selectors/missingDataFiles"
import { getClassifications } from "helpers/classification"
import { FIELDS, createAction, convertOptimizationTradesToActions } from "helpers/simulator"
import { getSelectedExperiment } from "modules/edit-experiment/selectors"
import { getNumberOfTrades } from "./selectors/data"
import { getSelectedContentSet } from "./selectors/exposure"
import { getForecast, getForecastSecurities } from "./selectors/forecast"
import { getIsOpPro } from "modules/account-root/selectors"
import { rebalanceQuery, rebalanceOnlyForecast } from "./helpers/queries/rebalance"
import { getHistoricalFactorSensitivityUnavailable } from "./selectors/factorSensitivity/historical"
import { DESC } from "helpers/sorts"
import { isGroupedByMembership } from "helpers/rollups"
import { getVariables } from "./helpers/queries/helpers"
import { computeUpdatedFilters } from "helpers/filters"
import { getUserPreference } from "helpers/userPreferences"
import { getAttributionMethod } from "./selectors/risk/index"
import { getFilter, getNormalization, getSegment } from "./selectors/filters"
import { createDefaultReducer } from "helpers/reducers"
import { getFilter as computeFilter } from "helpers/assets"
import { addOrReplaceAction } from "helpers/simulator"
import { handleSetSingleDate } from "helpers/date-preferences"
import { removeSimulatorTrades } from "helpers/simulator-trade-storage"
import { getSimulatorRouteMode, getTradePortfolioId, isPortfolioTradeContextSettled, readPendingPortfolioTrades } from "./helpers/trade-storage"

function updatePreferences(state) {
  return {
    type: "simulatorSettings",
    key: getPortfolioId(state),
    value: getSimulatorSettings(state),
  }
}

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}
const SCREEN_NAME = "Rebalance > Simulator"

export const { Types, Creators } = actionCreator("ompnt/modules/rebalance", {
  showSecurityDetails: {
    props: ["security"],
    track: false,
  },
  rebalance: {
    props: ["change", "originalValue"],
    track: false,
  },
  rebalanceSuccess: {
    props: ["updatedMetrics"],
    track: false,
  },
  rebalanceContentSet: {
    props: ["exposure", "contributors", "groupedContributors"],
  },
  rebalanceError: {
    props: [],
    track: false,
  },
  hideSecurityDetails: {
    props: [],
    track: false,
  },
  setSorting: {
    props: ["order"],
    track: true,
    trackType: "simulator-sort-updated",
    trackPayload: ({ order }) => pick(order, ["type", "id", "direction"]),
  },
  loadTrades: {
    props: ["rebalance"],
    track: false,
  },
  resetRebalance: {
    props: [],
    track: true,
    trackType: "simulator-reset-trades",
  },
  setSelectedCategory: {
    props: ["category"],
    track: true,
    trackType: "simulator-set-selected-category",
  },
  setDecompositionType: {
    props: ["decompositionType"],
    track: true,
    trackType: "simulator-set-view",
  },
  setMetricType: {
    props: ["metricType"],
    track: true,
    trackType: "simulator-set-metric-type",
  },
  setSelectedDataType: {
    props: ["selectedDataType"],
    track: true,
    trackType: "simulator-set-selected-data-type",
  },
  setForecast: {
    props: ["data"],
    track: true,
    trackType: "simulator-set-forecast",
    updatePreference: {
      getItems: (state) => [updatePreferences(state)],
    },
  },
  setForecastSuccess: {
    props: [],
    track: false,
  },
  rebalanceForecast: {
    props: ["contributors", "groupedContributors"],
    track: false,
  },
  loadExperiment: {
    props: [],
    track: false,
  },
  loadExperimentConfig: {
    props: ["experimentConfig"],
    track: false,
  },
  restoreTrades: {
    props: ["pendingTrades"],
    track: false,
  },
  trackExperiment: {
    props: ["experiment"],
    track: true,
    trackType: "simulator-load-experiment",
    trackPayload: ({ experiment }) => pick(experiment, ["accountId", "name", "description", "type", "lastUpdated"]),
  },
  toggleNotHeld: {
    props: [],
    track: true,
    trackType: "simulator-toggle-not-held",
  },
  setIsIntradayRebalance: {
    props: ["intraday"],
    track: false,
  },
  setEditSecurity: {
    props: ["security"],
    track: false,
  },
  setIntradayMeta: {
    props: ["metadata"],
    track: false,
  },
  reloadIntraday: {
    props: [],
    track: false,
  },
  setPage: {
    props: ["page"],
    track: true,
    trackType: "simulator-page-updated",
    trackPayload: ({ page }) => ({ page }),
  },
  setGrouping: {
    props: ["grouping"],
    track: true,
    trackType: "simulator-grouping-option-selected",
    trackPayload: ({ grouping }) => {
      let by
      if (grouping) {
        by = grouping.classificationId
          ? `${grouping.classificationId} - ${grouping.id}` : grouping.id
      }
      return { by }
    },
  },
  setSelectedGroup: {
    props: ["id"],
    track: true,
    trackType: "simulator-grouping-selected",
    trackPayload: ({ id }) => ({ id }),
  },
  hideGroup: [],
  setActionsBase: ["base"],
  newSimulateAction: {
    props: ["position", "field", "value", "newTrade", "isAddTradeWorkflow"],
    track: true,
    trackType: "simulator-edit-trades",
    trackPayload: {},
  },
  selectSearchItem: ["searchItem"],
  setSelectedPositionWithAdv: ["position"],
  updateFilters: {
    props: ["filter"],
    track: true,
    trackType: "simulator-segment-filter-updated",
    trackPayload: ({ filters }) => ({ filters }),
  },
  toggleFilters: [],
  setSimulateActions: ["simulateActions"],
  setSegment: ["segment"],
  addRunningQuery: ["query"],
  removeRunningQuery: ["query"],
  setDefaultMetricUnits: ["metricUnits"],
  setRebalanceDate: ["date"],
  startAddTrade: [],
})

const DEFAULT_STATE = {
  selected: null,
  error: null,
  order: null,
  rebalancedPositions: EMPTY_OBJECT,
  rebalancedPositionHoldings: EMPTY_OBJECT,
  openedTable: null,
  rebalancedPositionsDelta: EMPTY_ARRAY,
  rebalancedExposure: EMPTY_OBJECT,
  rebalancedExposureContributors: EMPTY_ARRAY,
  rebalancedBeta: EMPTY_OBJECT,
  rebalancedBetaContributors: EMPTY_ARRAY,
  rebalancedContentSetExposure: EMPTY_OBJECT,
  rebalancedContentSetContributors: EMPTY_OBJECT,
  rebalancedRisk: EMPTY_OBJECT,
  rebalancedRiskContributors: EMPTY_ARRAY,
  rebalancedDynamicBeta: EMPTY_OBJECT,
  rebalancedDynamicBetaContributors: EMPTY_ARRAY,
  rebalancedForecastContributors: EMPTY_ARRAY,
  rebalancedPredictedFactorSensitivity: EMPTY_ARRAY,
  rebalancedPredictedFactorSensitivityContributors: EMPTY_ARRAY,
  rebalancedHistoricalFactorSensitivity: EMPTY_ARRAY,
  rebalancedHistoricalFactorSensitivityContributors: EMPTY_ARRAY,
  decompositionType: null,
  metricType: null,
  selectedCategory: null,
  forecast: null,
  horizon: 252,
  includeNotHeld: false,
  isIntradayRebalance: undefined,
  intradayAsOf: undefined,
  intradayBatchId: undefined,
  editing: null,
  runningQueries: EMPTY_ARRAY,
  page: 0,
  sort: {
    id: "positionPercentEquity",
    direction: DESC,
  },
  grouping: null,
  selectedGroup: null,
  actionsBase: null,
  setSelectedPositionWithAdv: null,
  filter: null,
  rebalancedFilters: null,
  analyticsSegment: null,
  filtersVisible: false,
  simulateActions: EMPTY_ARRAY,
  defaultMetricUnits: EMPTY_OBJECT,
  addedFilters: EMPTY_ARRAY,
}

function handleShowSecurityDetails(state, { security, field, value }) {
  const newField = field ? { id: field, value } : get(state, "selectedField")
  return { ...state, selected: security, selectedField: newField }
}

function handleHideSecurityDetails(state) {
  return { ...state, selected: null }
}

function handleSetSorting(state, { order }) {
  return { ...state, order }
}

function handleRebalance(state) {
  const newAddedPositions = mapValues(state.addedPositions, position => omit(position, "addedInGroup"))

  return {
    ...state,
    addedPositions: newAddedPositions,
    reset: false,
  }
}

function handleSetEditSecurity(state, { security, field }) {
  if (security && includes(["rebalanced", "delta"], security.id || get(security, "isAggregate"))) {
    return state
  }

  return { ...state, editing: pick(security, ["id", "internalId", "assetClass", "modelProviderId"]), editingField: field }
}

function handleRebalanceSuccess(state, { updatedMetrics }) {
  return {
    ...state,
    baseUpdate: false,
    ...updatedMetrics,
  }
}

function handleRebalanceError(state) {
  return {
    ...state,
    error: "A technical error has happened. Please try again later.",
  }
}

function handleResetRebalance(state) {
  return {
    ...state,
    // Don't reset the properties used for displaying the correct UI
    ...omit(DEFAULT_STATE, ["decompositionType", "metricType", "selectedCategory", "openedTable", "forecast", "horizon", "widgetField", "intradayRefreshable", "intradayAsOf", "intradayBatchId", "includeNotHeld", "grouping"]),
    reset: true, // tracks that we reset the UI to cancel any rebalance triggered before the reset.
  }
}

function handleModelChange() {
  return {
    ...DEFAULT_STATE,
    reset: true,
  }
}

function handleSetSelectedCategory(state, { category }) {
  let newState
  if (category && category.id === "all") {
    newState = { selectedCategory: null, order: state.order }
  } else {
    newState = { selectedCategory: category, order: category ? state.order : null }
  }

  return { ...state, ...newState }
}

function handleSetDecompositionType(state, { decompositionType }) {
  return { ...state, decompositionType, metricType: null, selectedCategory: null }
}

function handleSetMetricType(state, { metricType }) {
  return { ...state, metricType }
}

function handleSetSelectedDataType(state, { selectedDataType }) {
  return { ...state, selectedDataType }
}

function handleSetForecast(state, { data }) {
  const { forecast, horizon } = isEmpty(data) ? pick(DEFAULT_STATE, ["forecast", "horizon"]) : data

  return { ...state, forecast, horizon }
}

function handleRebalanceForecast(state, { contributors, groupedContributors }) {
  return {
    ...state,
    rebalancedForecastContributors: contributors,
    rebalancedGroupedForecastContributors: groupedContributors,
  }
}

function handleLoadExperiment(state) {
  return { ...state }
}

function handleLoadExperimentConfig(state, { experimentConfig }) {
  const { changes, simulateActions, actionsBase } = experimentConfig ?? {}
  let newSimulateActions = simulateActions
  // Map Old experiment changes to new simulateActions
  if (changes) {
    newSimulateActions = map(changes, (trade) => createAction({ position: pick(trade, ["ticker", "mic", "assetClass"]), field: "percentEquity", value: trade.size * 100 }))
  }

  return {
    ...state,
    reset: false, // tracks that we reset the UI to cancel any rebalance triggered before the reset.
    selected: "",
    baseUpdate: false,
    selectedCategory: null,
    decompositionType: null,
    selectedDataType: "original",
    rebalancedPositions: EMPTY_ARRAY,
    rebalancedPositionHoldings: EMPTY_ARRAY,
    rebalancedExposure: EMPTY_ARRAY,
    rebalancedExposureContributors: EMPTY_ARRAY,
    error: false,
    simulateActions: newSimulateActions,
    actionsBase,
  }
}

function handleBaseUpdate(state) {
  return {
    ...state,
    baseUpdate: true,
  }
}

function handleToggleNotHeld(state) {
  return {
    ...state,
    includeNotHeld: !state.includeNotHeld,
  }
}

function handleRebalanceContentSet(state, { exposure, contributors, groupedContributors }) {
  return {
    ...state,
    rebalancedContentSetExposure: exposure,
    rebalancedContentSetContributors: contributors,
    rebalancedGroupedContentSetContributors: groupedContributors,
  }
}

function handleSetIsIntradayRebalance(state, { intraday }) {
  return {
    ...state,
    isIntradayRebalance: intraday,
  }
}

function handleReloadIntraday(state) {
  return {
    ...state,
    ...pick(DEFAULT_STATE, ["intradayAsOf"]),
  }
}

function handleSetIntradayMeta(state, { metadata }) {
  if (!metadata) {
    return {
      ...state,
      intradayAsOf: undefined,
      intradayBatchId: undefined,
    }
  }

  const { asOf, id } = metadata
  return {
    ...state,
    intradayAsOf: asOf,
    intradayBatchId: id,
  }
}

function handleSetPage(state, { page }) {
  return { ...state, page }
}

function handleSetGrouping(state, { grouping }) {
  return { ...state, grouping, selectedGroup: null }
}

function handleSetSelectedGroup(state, { id }) {
  return { ...state, selectedGroup: id }
}

function handleHideGroup(state) {
  return { ...state, selectedGroup: null }
}

function isNotOverweightUnderWeightGroup(grouping, selectedGroup) {
  // Overweight/underweight group still uses the id LONG_SHORT but the values are different [UNDERWEIGHT, OVERWEIGHT]
  return !(get(grouping, "id") === "LONG_SHORT" && includes(["OVERWEIGHT", "UNDERWEIGHT"], selectedGroup))
}

export function computeActionFilters(segment, grouping, selectedGroup, isAddTradeWorkflow) {
  let filters = {}
  if (segment && segment.filters) {
    filters = segment.filters
  }

  // We don't support group level trades when using the Add Trade workflow
  if (!isAddTradeWorkflow && grouping && isNotOverweightUnderWeightGroup(grouping, selectedGroup)) {
    let filter
    if ((grouping.isPositionAttribute || grouping.isTag) && selectedGroup === "Other") {
      filter = {
        [grouping.isPositionAttribute ? "positionAttributes" : "tag"]: { id: grouping.id, hasValue: false },
      }
    } else {
      filter = computeFilter({ id: selectedGroup, isDrillDown: true }, grouping, selectedGroup)
    }
    filters = {
      ...filters,
      ...filter,
    }
  }

  return isEmpty(filters) ? null : filters
}

function handleNewSimulateAction(state, { position, field, value, newTrade, isAddTradeWorkflow }) {
  const { analyticsSegment, grouping, selectedGroup } = pick(state, ["analyticsSegment", "grouping", "selectedGroup"])
  const filters = newTrade ? undefined : computeActionFilters(analyticsSegment, grouping, selectedGroup, isAddTradeWorkflow)
  const simulateActions = addOrReplaceAction(state.simulateActions, createAction({ position, field, value, filters }))
  return {
    ...state,
    reset: false,
    simulateActions,
  }
}

function handleSetSelectedPositionWithAdv(state, { position }) {
  return { ...state, selectedPositionWithAdv: position }
}

function handleSetActionsBase(state, { base }) {
  return { ...state, actionsBase: base }
}

function handleUpdateFilters(state, { filter }) {
  return { ...state, ...computeUpdatedFilters(filter) }
}

function handleToggleFilters(state) {
  return { ...state, filtersVisible: !state.filtersVisible }
}

function handleSetSelectedBase(state, { selectedBase }) {
  let newGrouping = state.grouping
  const isActive = !!selectedBase
  const newFilter = removeUnsupportedFilters(state.filter, isActive, NORMALIZATION.CONTRIBUTION)
  if (isActive && isGroupedByMembership(newGrouping)) {
    newGrouping = null
  }
  return { ...state, grouping: newGrouping, filter: newFilter }
}

function handleSetSegment(state, { segment }) {
  return {
    ...state,
    analyticsSegment: segment,
  }
}

function handleAddRunningQuery(state, { query }) {
  return {
    ...state,
    runningQueries: [...state.runningQueries, query],
  }
}

function handleRemoveRunningQuery(state, { query }) {
  return {
    ...state,
    runningQueries: filter(state.runningQueries, q => q !== query),
  }
}

function handleSetDefaultMetricUnits(state, { metricUnits }) {
  return {
    ...state,
    defaultMetricUnits: metricUnits,
  }
}

function handleSetRebalanceDate(state, { date }) {
  return { ...state, rebalanceDate: date }
}

export default createReducer(DEFAULT_STATE, {
  [Types.SET_SORTING]: handleSetSorting,
  [Types.SHOW_SECURITY_DETAILS]: handleShowSecurityDetails,
  [Types.HIDE_SECURITY_DETAILS]: handleHideSecurityDetails,
  [Types.REBALANCE]: handleRebalance,
  [Types.REBALANCE_SUCCESS]: handleRebalanceSuccess,
  [Types.REBALANCE_ERROR]: handleRebalanceError,
  [Types.REBALANCE_CONTENT_SET]: handleRebalanceContentSet,
  [Types.RESET_REBALANCE]: handleResetRebalance,
  [Types.SET_SELECTED_CATEGORY]: handleSetSelectedCategory,
  [Types.SET_DECOMPOSITION_TYPE]: handleSetDecompositionType,
  [Types.SET_METRIC_TYPE]: handleSetMetricType,
  [Types.SET_SELECTED_DATA_TYPE]: handleSetSelectedDataType,
  [Types.SET_FORECAST]: handleSetForecast,
  [Types.REBALANCE_FORECAST]: handleRebalanceForecast,
  [Types.LOAD_EXPERIMENT_CONFIG]: handleLoadExperimentConfig,
  [Types.LOAD_EXPERIMENT]: handleLoadExperiment,
  [Types.TOGGLE_NOT_HELD]: handleToggleNotHeld,
  [Types.SET_IS_INTRADAY_REBALANCE]: handleSetIsIntradayRebalance,
  [Types.SET_INTRADAY_META]: handleSetIntradayMeta,
  [Types.RELOAD_INTRADAY]: handleReloadIntraday,
  [Types.SET_EDIT_SECURITY]: handleSetEditSecurity,
  [Types.SET_PAGE]: handleSetPage,
  [Types.SET_GROUPING]: handleSetGrouping,
  [Types.SET_SELECTED_GROUP]: handleSetSelectedGroup,
  [Types.HIDE_GROUP]: handleHideGroup,
  [Types.SET_ACTIONS_BASE]: handleSetActionsBase,
  [Types.NEW_SIMULATE_ACTION]: handleNewSimulateAction,
  [Types.SET_SELECTED_POSITION_WITH_ADV]: handleSetSelectedPositionWithAdv,
  [Types.UPDATE_FILTERS]: handleUpdateFilters,
  [Types.TOGGLE_FILTERS]: handleToggleFilters,
  [Types.SET_SEGMENT]: handleSetSegment,
  [Types.SET_SIMULATE_ACTIONS]: createDefaultReducer(),
  [Types.ADD_RUNNING_QUERY]: handleAddRunningQuery,
  [Types.REMOVE_RUNNING_QUERY]: handleRemoveRunningQuery,
  [Types.SET_DEFAULT_METRIC_UNITS]: handleSetDefaultMetricUnits,
  [Types.SET_REBALANCE_DATE]: handleSetRebalanceDate,
  [PortfolioRootTypes.SET_SELECTED_BASE_SUCCESS]: handleBaseUpdate,
  [PortfolioRootTypes.SET_MODEL]: handleModelChange,
  [PortfolioRootTypes.SET_SELECTED_BASE]: handleSetSelectedBase,
})

function* getRebalanceParameters() {
  return yield select(state => ({
    account: state.global.selected.account,
    actionsBase: getActionsBase(state),
    analyticsSegment: getSegment(state),
    attributionMethod: getAttributionMethod(state),
    base: getBasePositionSet(state),
    betaInstrument: getPortfolioBetaInstrument(state),
    classifications: getClassifications(state),
    currency: getCurrency(state),
    decompositionType: getDecompositionType(state),
    forecastAndHorizon: getForecast(state),
    forecastSecurities: getForecastSecurities(state),
    groupingOptions: getGroupingOptions(state),
    historicalFactorSensitivityUnavailable: getHistoricalFactorSensitivityUnavailable(state),
    intradayBatchId: getIntradayBatchId(state),
    isActive: getIsActiveAndNotIntraday(state),
    isIntraday: getIsIntraday(state),
    isInvalidBetaInstrument: getIsInvalidBetaInstrumentReason(state),
    isOpPro: getIsOpPro(state),
    missingDataFiles: getMissingDataFiles(state),
    modelId: getSelectedModelId(state),
    portfolioAliasOrId: state.global.selected.portfolio,
    selectedContentSet: getSelectedContentSet(state),
    selectedDate: getRebalanceDate(state),
    simulateActions: getSimulateActions(state),
    expand: getIsExpanded(state),
  }))
}

function* submitRebalance(change, fromTrades) {
  const simulateActions = yield select(getSimulateActions)
  if (isEmpty(simulateActions)) {
    yield put(PortfolioRootCreators.removeModifiedScreen(SCREEN_NAME))
    return
  }
  const parameters = yield getRebalanceParameters()
  const reset = yield select(getReset)

  const { account, isIntraday, isActive, base, selectedContentSet, groupingOptions, actionsBase } = parameters
  // If fromTrades and isActive are true, it means we're loading Optimizer trades using setActivePercentEquity
  const needToSetActionsBase = (get(change, "field") === FIELDS.activePercentEquity && !actionsBase && isActive) || (fromTrades && isActive)
  yield put(Creators.setIsIntradayRebalance(isIntraday))
  yield put(Creators.addRunningQuery("rebalance"))

  if (needToSetActionsBase) {
    set(parameters, "actionsBase", base)
  }
  const query = rebalanceQuery(parameters)
  const variables = getVariables(parameters, query)
  const { apiMessages, data } = yield call(sendQuery, query, variables, account)
  if (apiMessages && !isEmpty(apiMessages.errors)) {
    yield put(Creators.rebalanceError())
  }

  const contentSetId = get(selectedContentSet, "escapedId")

  // Positions, still split by holding, ready to be uploaded as-is
  const rebalancedPositionHoldings = get(data, "model.simulateActions.positions")
  const rebalancedPositions = chain(data)
    .get("model.simulateActions")
    .pick(["composition", "percent"])
    .value()
  const rebalancedPositionsDelta = get(data, "model.simulateActions.positionsDelta")
  const rebalancedRisk = get(data, "model.simulateActions.risk.0")
  const rebalancedExposure = get(data, "model.simulateActions.exposure.0")
  const rebalancedBeta = get(data, "model.simulateActions.beta.0")
  const rebalancedDynamicBeta = get(data, "model.simulateActions.beta.0")
  const rebalancedRiskContributors = get(data, "model.simulateActions.riskContributors")
  const rebalancedExposureContributors = get(data, "model.simulateActions.exposureContributors.0.contributors")
  const rebalancedBetaContributors = get(data, "model.simulateActions.betaContributors.0.contributors")
  const rebalancedDynamicBetaContributors = get(data, "model.simulateActions.dynamicBetaContributors.0.contributors")
  const rebalancedPredictedFactorSensitivity = get(data, "model.simulateActions.predictedFactorSensitivity")
  const rebalancedPredictedFactorSensitivityContributors = get(data, "model.simulateActions.predictedFactorSensitivityContributors")
  const rebalancedHistoricalFactorSensitivity = get(data, "model.simulateActions.historicalFactorSensitivity")
  const rebalancedHistoricalFactorSensitivityContributors = get(data, "model.simulateActions.historicalFactorSensitivityContributors")
  const rebalancedForecastContributors = get(data, "model.simulateActions.forecastContributors.contributors")
  const rebalancedFilters = get(data, "model.simulateActions.filters")

  let rebalancedGroupedRiskContributors, rebalancedGroupedExposureContributors, rebalancedGroupedBetaContributors, rebalancedGroupedDynamicBetaContributors, rebalancedGroupedPredictedFactorSensitivityContributors, rebalancedGroupedHistoricalFactorSensitivityContributors, rebalancedGroupedForecastContributors, rebalancedGroupedPositions
  if (groupingOptions) {
    rebalancedGroupedRiskContributors = get(data, "model.simulateActions.groupedRiskContributors")
    rebalancedGroupedExposureContributors = get(data, "model.simulateActions.groupedExposureContributors.0.groupedContributors")
    rebalancedGroupedBetaContributors = get(data, "model.simulateActions.groupedBetaContributors.0.groupedContributors")
    rebalancedGroupedDynamicBetaContributors = get(data, "model.simulateActions.groupedDynamicBetaContributors.0.groupedContributors")
    rebalancedGroupedPredictedFactorSensitivityContributors = get(data, "model.simulateActions.groupedPredictedFactorSensitivityContributors")
    rebalancedGroupedHistoricalFactorSensitivityContributors = get(data, "model.simulateActions.groupedHistoricalFactorSensitivityContributors")
    rebalancedGroupedForecastContributors = get(data, "model.simulateActions.groupedForecastContributors.groupedContributors")
    rebalancedGroupedPositions = chain(data)
      .get("model.simulateActions")
      .pick(["groupedComposition", "groupedPercent"])
      .value()
  }

  let rebalancedContentSetContributors, rebalancedContentSetExposure, rebalancedGroupedContentSetContributors
  if (contentSetId) {
    rebalancedContentSetContributors = { [contentSetId]: get(data, ["model", "simulateActions", contentSetId]) }
    rebalancedContentSetExposure = { [contentSetId]: get(data, ["model", "simulateActions", `${contentSetId}_exposure`]) }
    rebalancedGroupedContentSetContributors = { [`${contentSetId}_grouped`]: get(data, ["model", "simulateActions", `${contentSetId}_grouped`]) }
  }

  yield put(PortfolioRootCreators.setModifiedScreen(SCREEN_NAME))

  if (!reset || fromTrades) {
    // ActionsBase
    if (needToSetActionsBase) {
      yield put(Creators.setActionsBase(base))
    }

    yield put(Creators.rebalanceSuccess({
      rebalancedPositions,
      rebalancedPositionHoldings,
      rebalancedPositionsDelta,
      rebalancedRisk,
      rebalancedExposure,
      rebalancedBeta,
      rebalancedDynamicBeta,
      rebalancedRiskContributors,
      rebalancedExposureContributors,
      rebalancedBetaContributors,
      rebalancedDynamicBetaContributors,
      rebalancedContentSetContributors,
      rebalancedContentSetExposure,
      rebalancedForecastContributors,
      rebalancedPredictedFactorSensitivity,
      rebalancedPredictedFactorSensitivityContributors,
      rebalancedHistoricalFactorSensitivity,
      rebalancedHistoricalFactorSensitivityContributors,
      rebalancedGroupedRiskContributors,
      rebalancedGroupedExposureContributors,
      rebalancedGroupedBetaContributors,
      rebalancedGroupedDynamicBetaContributors,
      rebalancedGroupedContentSetContributors,
      rebalancedGroupedPredictedFactorSensitivityContributors,
      rebalancedGroupedHistoricalFactorSensitivityContributors,
      rebalancedGroupedPositions,
      rebalancedGroupedForecastContributors,
      rebalancedFilters,
    }))
  }
  yield put(Creators.removeRunningQuery("rebalance"))
}

function* startRebalance({ change = {} } = {}) {
  const simulateActions = yield select(getSimulateActions)

  try {
    if (!isEmpty(simulateActions) || !isEmpty(change)) {
      if (Number.isFinite(change.value)) {
        yield submitRebalance(change, false)
      }
    }
  } catch {
    yield put(Creators.rebalanceError())
  }
}

export function* handleLoadSuccess(action) {
  if (action.resource === "portfolio") {
    const state = yield select()
    const pendingTrades = yield call(readPendingPortfolioTrades, state)
    yield put(Creators.resetRebalance())
    if (pendingTrades) {
      yield put(Creators.restoreTrades(pendingTrades))
    }
  }
  if (action.resource === "simulatorPreferences") {
    const { forecast } = getUserPreference(action.data, "simulatorSettings", null) ?? {}
    yield put(Creators.setForecast(forecast))
    const defaultMetricUnits = getUserPreference(action.data, "defaultMetricUnits", {})
    yield put(Creators.setDefaultMetricUnits(defaultMetricUnits))
  }
}

export const RESTORE_TRADES_TIMEOUT_MS = 120000

function isRestoreWaitEnded(action, portfolioId) {
  if (action.type === Types.RESET_REBALANCE) return true
  return action.type === HistoryTypes.NAVIGATE && getPortfolioIdFromPathname(action.location?.pathname) !== portfolioId
}

function isRestoreRebalanceSuperseded(action) {
  if (action.type === Types.RESET_REBALANCE || action.type === Types.NEW_SIMULATE_ACTION) return true
  return action.type === Types.REBALANCE && Number.isFinite(action.change?.value)
}

const getCurrentPathname = () => window.location.pathname

function* waitForRestoreRoute({ context }) {
  let route = getSimulatorRouteMode(yield call(getCurrentPathname))
  while (true) {
    if (route && route !== context.mode) return "discard"
    if (route === context.mode || context.mode !== "intraday") return "restore"
    const action = yield take([Types.RESET_REBALANCE, HistoryTypes.NAVIGATE])
    if (isRestoreWaitEnded(action, context.portfolioId)) return "stop"
    route = getSimulatorRouteMode(action.location?.pathname)
  }
}

function isOtherSimulatorNavigation(action, mode) {
  if (action.type !== HistoryTypes.NAVIGATE) return false
  const route = getSimulatorRouteMode(action.location?.pathname)
  return route !== null && route !== mode
}

function* waitForRestoreContext({ context }) {
  while (true) {
    const portfolioId = yield select(getTradePortfolioId)
    const simulateActions = yield select(getSimulateActions)
    if (portfolioId !== context.portfolioId || !isEmpty(simulateActions)) return "stop"
    if (yield select(isPortfolioTradeContextSettled, context)) return "restore"
    const action = yield take("*")
    if (isRestoreWaitEnded(action, context.portfolioId)) return "stop"
    if (isOtherSimulatorNavigation(action, context.mode)) return "discard"
  }
}

export function* handleRestoreTrades({ pendingTrades }) {
  const route = yield call(waitForRestoreRoute, pendingTrades)
  if (route === "discard") {
    yield call(removeSimulatorTrades, pendingTrades.key)
    return
  }
  if (route !== "restore") return

  const { outcome } = yield race({
    outcome: call(waitForRestoreContext, pendingTrades),
    timeout: delay(RESTORE_TRADES_TIMEOUT_MS),
  })
  if (outcome === "discard") {
    yield call(removeSimulatorTrades, pendingTrades.key)
    return
  }
  if (outcome !== "restore") return

  yield put(Creators.loadExperimentConfig({ simulateActions: pendingTrades.trades, actionsBase: pendingTrades.actionsBase }))
  try {
    yield race({
      rebalanced: call(submitRebalance),
      superseded: take(isRestoreRebalanceSuperseded),
    })
  } catch {
    yield put(Creators.rebalanceError())
  }
}

function* handleGQLLoadSuccess({ resource, data }) {
  const { selectedGroup } = yield select(state => ({
    selectedGroup: getSelectedGroup(state),
  }))

  if (resource === "simulatorGroupedComposition" && selectedGroup) {
    const selectedGroupExists = chain(data)
      .get("model.simulation.groupedComposition.0.compositionBy", [])
      .some(group => group.id === selectedGroup)
      .value()

    if (!selectedGroupExists) {
      yield put(Creators.hideGroup())
    }
  }

  if (resource === "forecastContributors") {
    yield put(Creators.setForecastSuccess())
  }
}

function* handleReset() {
  yield put(PortfolioRootCreators.removeModifiedScreen(SCREEN_NAME))
}

function* fetchExperiment() {
  const experiment = yield select(getSelectedExperiment)
  if (experiment) {
    yield put(Creators.trackExperiment(experiment))
    yield submitRebalance()
  }
}

function* handleIntradayReload() {
  yield put(modelCreators.resetCollections(["simulatorPositionHoldings", "simulatorComposition", "beta", "betaContributors", "dynamicBeta", "dynamicBetaContributors", "exposure", "exposureContributors", "contentSetExposure", "contentSetContributors", "risk", "riskContributors", "forecastContributors"]))
}

function* updateSegment() {
  const { filter, normalization } = yield select(state => ({
    filter: getFilter(state),
    normalization: getNormalization(state),
  }))
  yield put(Creators.setSegment(computeFilteredSegment(filter, normalization)))
}

function* handleBaseChangeStateAdjustement() {
  const simulateActions = yield select(getSimulateActions)
  yield updateSegment()

  if (simulateActions) {
    yield submitRebalance()
  }
}

function* handleGroupingRebalance({ grouping }) {
  if (isEmpty(grouping)) { return }

  const simulateActions = yield select(getSimulateActions)

  if (!isEmpty(simulateActions)) {
    yield submitRebalance()
  }
}

function* handleFilterRebalance() {
  const simulateActions = yield select(getSimulateActions)
  yield updateSegment()

  if (!isEmpty(simulateActions)) {
    yield submitRebalance()
  }
}

function* newSimulateAction({ position, field, value }) {
  const change = {
    mic: position.exchange,
    ...pick(position, ["ticker", "mic", "modelProviderId", "country", "assetClass", "internalId", "id"]),
    field,
    value: parseFloat(value),
  }

  yield startRebalance({ change })
}

function* getSecurityADV({ searchItem }) {
  const { account, modelId, selectedDate } = yield select(state => ({
    account: state.global.selected.account,
    selectedDate: getRebalanceDate(state),
    modelId: getSelectedModelId(state),
  }))

  const query = `query ($modelId: String! $ticker: String, $mic: String $modelProviderId: String, $internalId: String $on: Date!) {
    model(id: $modelId) {
      security(
        ticker: $ticker
        mic: $mic
        modelProviderId: $modelProviderId
        internalId: $internalId
      ) {
        descriptors(on: $on) {
          averageDailyVolume
        }
      }
    }
  }`
  const variables = {
    modelId,
    on: selectedDate,
    ...pick(searchItem, ["ticker", "internalId", "mic", "modelProviderId"]),
    ...(searchItem.exchange && { mic: searchItem.exchange }),
  }
  const { data } = yield call(sendQuery, query, variables, account)
  const { averageDailyVolume } = get(data, "model.security.descriptors", {})
  yield put(Creators.setSelectedPositionWithAdv({ ...searchItem, averageDailyVolume }))
}

function* handleRebalancedForecastChange() {
  const parameters = yield getRebalanceParameters()
  const { account, simulateActions } = parameters
  // If there are no simulateActions, it means there is no rebalance query to be executed
  if (isEmpty(simulateActions)) { return }

  const query = rebalanceOnlyForecast(parameters)
  const variables = getVariables(parameters, query)
  yield put(Creators.addRunningQuery("forecast"))
  const { apiMessages, data } = yield call(sendQuery, query, variables, account)
  if (apiMessages && !isEmpty(apiMessages.errors)) {
    yield put(Creators.rebalanceError())
  }

  const contributors = get(data, "model.simulateActions.forecastContributors.contributors")
  const groupedContributors = get(data, "model.simulateActions.groupedForecastContributors.groupedContributors")

  yield put(Creators.rebalanceForecast(contributors, groupedContributors))
  yield put(Creators.removeRunningQuery("forecast"))
}

function* handleLoadTrades({ rebalance }) {
  const isActive = yield select(getIsActiveAndNotIntraday)
  const field = isActive ? FIELDS.activePercentEquity : FIELDS.percentEquity

  yield put(Creators.setSimulateActions(convertOptimizationTradesToActions(rebalance, field)))
  yield submitRebalance(null, true)
}

function* updateDefaultMetrics({ metrics }) {
  yield put(Creators.setDefaultMetricUnits(metrics))
}

function* handleSetEditSecuritySaga({ security }) {
  if (security) {
    yield call(handleStartAddTrade)
  }
}

function* handleStartAddTrade() {
  const [numberOfTrades, rebalanceDate, endDate, isIntradayRoute] = yield all([
    select(getNumberOfTrades),
    select(getRebalanceDate),
    select(getEndDate),
    select(getIsIntradayRoute),
  ])

  if (!isIntradayRoute && numberOfTrades === 0 && rebalanceDate !== endDate) {
    yield put(NotificationCreators.add("warning", "Simulation date out of sync", "The simulation date does not match the analysis date from other screens. Please check the simulation date before proceeding with trades."))
    return
  }
}

export function* saga() {
  yield takeLatest(LoaderTypes.LOAD_SUCCESS, handleLoadSuccess)
  yield takeLatest(Types.RESTORE_TRADES, handleRestoreTrades)
  yield takeEvery([Types.REBALANCE, Types.SET_FORECAST], startRebalance)
  yield takeLatest([Types.LOAD_EXPERIMENT], fetchExperiment)
  yield takeLatest(Types.RESET_REBALANCE, handleReset)
  yield takeLatest(Types.RELOAD_INTRADAY, handleIntradayReload)
  yield takeEvery([Types.SET_DECOMPOSITION_TYPE, PortfolioRootTypes.SET_EXPANSION_MODE], submitRebalance)
  yield takeEvery(PortfolioRootTypes.SET_SELECTED_BASE_SUCCESS, handleBaseChangeStateAdjustement)
  yield takeEvery(Types.SET_GROUPING, handleGroupingRebalance)
  yield takeEvery(Types.NEW_SIMULATE_ACTION, newSimulateAction)
  yield takeEvery(Types.SELECT_SEARCH_ITEM, getSecurityADV)
  yield takeEvery([Types.SET_FORECAST_SUCCESS], handleRebalancedForecastChange)
  yield takeLatest(GQLLoaderTypes.LOAD_SUCCESS, handleGQLLoadSuccess)
  yield takeEvery([Types.UPDATE_FILTERS], handleFilterRebalance)
  yield takeEvery(Types.LOAD_TRADES, handleLoadTrades)
  yield takeLatest(Types.SET_REBALANCE_DATE, handleSetSingleDate)
  yield takeLatest(Types.START_ADD_TRADE, handleStartAddTrade)
  yield takeLatest(Types.SET_EDIT_SECURITY, handleSetEditSecuritySaga)
  yield takeLatest(DefaultMetricTypes.UPDATE_DEFAULT_METRICS, updateDefaultMetrics)
}
